import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Check,
  Clock3,
  LoaderCircle,
  RefreshCw,
  User
} from 'lucide-react';

const OPERATION_NAMES = {
  80: 'DEVICE INFO',
  82: 'DEVICE RESTART',
  83: 'PICTURE CAPTURED',
  84: 'ON DEMAND TEST',
  85: 'VIOLATION RESET',
  86: 'TIME SET',
  87: 'DEVICE RESET',
  88: 'VEHICLE START',
  89: 'VEHICLE STOP',
  90: 'TAMPER ENABLE',
  91: 'TAMPER DISABLE',
  92: 'LOG READ',
  94: 'DEVICE CONFIG',
  103: 'SAMPLER OTA',
  110: 'CHANGE PING FREQUENCY'
};

const getOperationName = (operation) =>
  OPERATION_NAMES[String(operation)] || operation || 'UNKNOWN';

const formatOperationTime = (value) => {
  if (!value) return { date: 'N/A', time: '' };
  const parts = String(value).trim().split(/\s+/);
  return {
    date: parts[0] || 'N/A',
    time: parts[1] ? parts[1].slice(0, 8) : ''
  };
};

const LastOperationProgress = ({ client, firmwareVersion }) => {
  const [operations, setOperations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const requestRef = useRef(0);

  const fetchOperations = useCallback(async () => {
    if (!client) return;

    const requestId = ++requestRef.current;
    const deviceId = Number(firmwareVersion) > 34
      ? client.cbid
      : client.devId || client.cbid;

    setOperations([]);
    setError('');

    if (!deviceId) {
      setError('No device identifier available');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        `https://alcobrakeadmin.com/v3/app/v2data/getdeviceoperations?devid=${encodeURIComponent(deviceId)}`
      );
      if (!response.ok) {
        throw new Error('Unable to load device operations');
      }

      const data = await response.json();
      if (requestId !== requestRef.current) return;

      setOperations(
        Array.isArray(data)
          ? [...data].sort((a, b) => Number(b.id || 0) - Number(a.id || 0))
          : []
      );
    } catch (fetchError) {
      if (requestId !== requestRef.current) return;
      setError(fetchError.message || 'Unable to load device operations');
    } finally {
      if (requestId === requestRef.current) {
        setLoading(false);
      }
    }
  }, [client, firmwareVersion]);

  useEffect(() => {
    fetchOperations();
    return () => {
      requestRef.current += 1;
    };
  }, [fetchOperations]);

  if (loading) {
    return (
      <div className="last-operation-state">
        <LoaderCircle className="last-operation-spinner" size={34} />
        <span>Loading operations...</span>
      </div>
    );
  }

  if (error || !operations.length) {
    return (
      <div className="last-operation-state">
        <span>{error || 'No device operations available'}</span>
        <button type="button" onClick={fetchOperations}>
          <RefreshCw size={15} />
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="last-operation-list">
      {operations.map(operation => {
        const triggeredTime = formatOperationTime(operation.indiatime);
        const resultTime = formatOperationTime(operation.actionindiatime);
        const isSuccess = String(operation.status).toUpperCase() === 'SUCCESS';
        const steps = [
          {
            label: 'Action Triggered',
            date: triggeredTime.date,
            time: triggeredTime.time,
            complete: true
          },
          {
            label: 'Result Time',
            date: resultTime.date,
            time: resultTime.time,
            complete: isSuccess
          },
          {
            label: 'Result',
            date: operation.status || 'PENDING',
            time: '',
            complete: isSuccess
          }
        ];

        return (
          <article className="last-operation-card" key={operation.id}>
            <header className="last-operation-header">
              <div>
                <span className="last-operation-label">Operation</span>
                <strong>{getOperationName(operation.operation)}</strong>
              </div>
              <span className={`last-operation-status ${isSuccess ? 'success' : ''}`}>
                {operation.status || 'PENDING'}
              </span>
            </header>

            <div className="last-operation-triggered">
              <User size={15} />
              <span>Triggered by</span>
              <strong>{operation.triggeredBy || 'N/A'}</strong>
            </div>

            <div className="last-operation-progress">
              {steps.map((step, index) => (
                <div
                  className={`last-operation-step ${step.complete ? 'complete' : ''}`}
                  key={step.label}
                >
                  <div className="last-operation-step-track">
                    <span className="last-operation-step-marker">
                      {step.complete ? <Check size={14} /> : <Clock3 size={13} />}
                    </span>
                    {index < steps.length - 1 && (
                      <span className="last-operation-step-line"></span>
                    )}
                  </div>
                  <div className="last-operation-step-copy">
                    <span>{step.label}</span>
                    <strong>{step.date}</strong>
                    {step.time && <small>{step.time}</small>}
                  </div>
                </div>
              ))}
            </div>
          </article>
        );
      })}
    </div>
  );
};

export default LastOperationProgress;
